import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";

const cardShell =
  "rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white p-2 shadow-sm ring-1 ring-slate-200/50";

const soldTone = {
  30: "from-emerald-50/80 to-white ring-emerald-200/50 text-emerald-900",
  60: "from-sky-50/80 to-white ring-sky-200/50 text-sky-900",
  90: "from-indigo-50/80 to-white ring-indigo-200/50 text-indigo-900",
} as const;

function formatSoldCount(
  n: number | null | undefined
): { display: string; has: boolean } {
  if (n === null || n === undefined) return { display: "—", has: false };
  return { display: n.toLocaleString(), has: true };
}

/**
 * Resolves a single “in stock” figure for the summary: explicit API
 * `inStockCount` when present, else category `internalStock` when present,
 * else offer quantity, else 1 for an unsold line item, 0 if sold.
 */
function resolveInStockCount(part: IPart): number {
  const withStock = part as IPart & { inStockCount?: number | null };
  if (
    withStock.inStockCount !== undefined &&
    withStock.inStockCount !== null
  ) {
    return Math.max(0, withStock.inStockCount);
  }
  const catInternal = (
    part.category as { internalStock?: number | null } | null | undefined
  )?.internalStock;
  if (catInternal !== undefined && catInternal !== null) {
    return Math.max(0, catInternal);
  }
  if (part.soldDate) return 0;
  const oq = part.offers?.[0]?.quantity;
  if (typeof oq === "number" && oq >= 0) return oq;
  return 1;
}

type Props = { part: IPart };

export default function UnlistedStockSummaryCell({ part }: Props) {
  const inStock = resolveInStockCount(part);
  const s30 = formatSoldCount(part.similarSoldLast30);
  const s60 = formatSoldCount(part.similarSoldLast60);
  const s90 = formatSoldCount(part.similarSoldLast90);

  return (
    <div
      className="flex h-full w-full min-w-0 max-w-xs flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={classNames(cardShell, "flex h-full w-full flex-col")}>
        <div
          className="rounded-lg bg-gradient-to-b from-slate-100/80 to-white px-2 py-1 ring-1 ring-inset ring-slate-200/70"
          title={`${inStock.toLocaleString()} in stock`}
        >
          <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-600/80">
            In stock
          </div>
          <div className="text-base font-semibold tabular-nums leading-tight text-slate-900">
            {inStock.toLocaleString()}
          </div>
        </div>

        <div className="mt-1 grid grid-cols-3 gap-1">
          {(
            [
              { key: "30", label: "30d", v: s30, tone: soldTone[30] },
              { key: "60", label: "60d", v: s60, tone: soldTone[60] },
              { key: "90", label: "90d", v: s90, tone: soldTone[90] },
            ] as const
          ).map(({ key, label, v, tone }) => (
            <div
              key={key}
              className={classNames(
                "rounded-lg bg-gradient-to-b px-1.5 py-1 ring-1 ring-inset",
                tone
              )}
              title={`Similar parts sold in the last ${label}`}
            >
              <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">
                {label}
              </div>
              <div
                className={classNames(
                  "text-sm font-semibold tabular-nums leading-tight",
                  v.has ? "" : "text-slate-400"
                )}
              >
                {v.display}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
